<template>
  <div class="h-full flex flex-col bg-[#f8fafc] gap-4 overflow-auto pr-2">
    <div class="print-hide bg-white rounded-xl shadow-sm border border-slate-200 overflow-visible border-t-4 border-t-[#0d4169]">
      <div class="px-6 pt-5 pb-3">
        <h2 class="text-base font-bold text-[#0d4169]">SIV Report</h2>
      </div>
      <form @submit.prevent="generateReport" class="px-6 pb-4 pt-2">
        <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Start Date <span v-if="isStartDateRequired" class="text-red-500">*</span></label>
            <input
              v-model="filters.startDate"
              type="date"
              :required="isStartDateRequired"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">End Date</label>
            <input
              v-model="filters.endDate"
              type="date"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Category</label>
            <select
              v-model="filters.categoryId"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
              <option :value="null">All</option>
              <option value="product">Product</option>
              <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Department</label>
            <select
              v-model="filters.departmentId"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
              <option :value="null">All</option>
              <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
            </select>
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Item</label>
            <input
              v-model="filters.itemQuery"
              type="text"
              placeholder="Search item/product..."
              list="siv-report-item-suggestions"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
            <datalist id="siv-report-item-suggestions">
              <option v-for="name in itemSuggestions" :key="name" :value="name" />
            </datalist>
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">SIV Number</label>
            <input
              v-model="filters.sivNo"
              type="text"
              placeholder="e.g. SIV-0001"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Is Ingredient</label>
            <select
              v-model="filters.ingredientFilter"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
              <option value="all">All</option>
              <option value="1">Ingredient</option>
              <option value="0">Material</option>
            </select>
          </div>
        </div>

        <div class="mt-3 pt-3 pr-8 border-t border-slate-100 flex justify-between items-center gap-3">
          <button
            type="button"
            @click="handlePrintReport"
            class="px-4 py-1.5 bg-slate-200 text-slate-700 text-sm font-bold rounded-md shadow-md hover:bg-slate-300 transition-all active:scale-95 disabled:opacity-60 flex items-center gap-2"
            :disabled="loading"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 9V2h12v7"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            <span>Print</span>
          </button>
          <button type="submit" class="px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95">
            <div v-if="loading" class="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"></div>
            <span v-else>Generate </span> 
          </button>
        </div>
      </form>
    </div>

    <div class="w-full overflow-x-auto shadow-sm rounded-xl pa-1 print-container siv-table-wrap">
      <div class="hidden print:block mb-6">
        <div class="print-doc-header print-doc-header-grid">
          <div class="print-doc-left">
            <div class="print-company-block">
              <img
                v-if="printCompanyLogo"
                :src="printCompanyLogo"
                :alt="printCompanyName || 'Company Logo'"
                class="print-company-logo"
              />
              <div v-else class="print-company-logo-fallback">Logo</div>
              <div class="print-company-text">
                <div class="print-company-name">{{ printCompanyName }}</div>
              </div>
            </div>
          </div>
          <h1 class="print-doc-title text-xl font-bold text-center mb-2">SIV Report</h1>
          <div class="print-doc-meta print-doc-right">
            <div><strong>Date:</strong> {{ new Date().toLocaleDateString() }}</div>
          </div>
        </div>
        <hr class="my-4 border-black" />
      </div>

      <table class="w-full border-collapse bg-white overflow-hidden print-table">
        <thead>
          <tr>
            <th class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider">#</th>
            <th @click="sortBy('sivNo')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              Siv No <span v-if="sortKey === 'sivNo'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('reqDate')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              Req. Date <span v-if="sortKey === 'reqDate'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('apprDate')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              Appr. Date <span v-if="sortKey === 'apprDate'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('date')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              Tr. Date <span v-if="sortKey === 'date'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('departmentName')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              Department <span v-if="sortKey === 'departmentName'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('categoryName')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              Category <span v-if="sortKey === 'categoryName'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('itemName')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              Item <span v-if="sortKey === 'itemName'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('quantity')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              Qty <span v-if="sortKey === 'quantity'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('unitPrice')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              Unit Price <span v-if="sortKey === 'unitPrice'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('volume')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              Volume <span v-if="sortKey === 'volume'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('totalPrice')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              Total Price <span v-if="sortKey === 'totalPrice'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('remark')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              Remark <span v-if="sortKey === 'remark'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in sortedRows" :key="row.key" class="bg-white border-b">
            <td class="px-2 py-1 text-slate-700 text-center">{{ i + 1 }}</td>
            <td class="px-2 py-1 text-slate-700 text-center">{{ row.sivNo }}</td>

            <td class="px-2 py-1 text-slate-700 text-center">{{ row.reqDate }}</td>
            <td class="px-2 py-1 text-slate-700 text-center">{{ row.apprDate }}</td>
            <td class="px-2 py-1 text-slate-700 text-center">{{ row.date }}</td>
            <td class="px-2 py-1 text-slate-700 text-center">{{ row.departmentName || '-' }}</td>
            <td class="px-2 py-1 text-slate-700 text-center">{{ row.categoryName || '-' }}</td>
            <td class="px-2 py-1 text-center" :class="row.isProduct ? 'text-green-600 font-semibold' : 'text-slate-700'">{{ row.itemName }}</td>
            <td class="px-2 py-1 text-[#0d4169] italic font-semibold text-center">{{ row.quantity }}</td>
            <td class="px-2 py-1 text-[#0d4169] italic font-semibold text-center">{{money(row.unitPrice.toFixed(2)) }}</td>
            <td class="px-2 py-1 text-[#0d4169] italic font-semibold text-center">{{ row.volume }}</td>
            <td class="px-2 py-1 text-[#0d4169] italic font-semibold text-center">{{money(row.totalPrice.toFixed(2)) }}</td>
            <td class="px-2 py-1 text-slate-700 text-center">{{ row.remark || '-' }}</td>
          </tr>
          <tr v-if="sortedRows.length > 0" class="bg-slate-50 border-t-2 border-slate-300">
            <td colspan="10"></td>
            <td class="px-2 py-1 text-right font-bold text-slate-700">Total:</td>
            <td class="px-2 py-1 font-bold text-[#0d4169] text-center">{{money(totalPriceSum) }}</td>
            <td></td>
          </tr>
          <tr v-if="sortedRows.length === 0">
            <td colspan="13" class="px-4 py-10 text-center text-slate-400 italic text-sm">No data found for the selected filters.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import store from '../../../store'
import { apiFetch } from '../../../utils/api'

const departments = ref([])
const categories = ref([])
const productNames = ref([])
const itemNames = ref([])
const report = ref([])
const loading = ref(false)
const sortKey = ref('sivNo')
const sortOrder = ref('asc')
const filters = ref({
  startDate: "",
  endDate: '',
  categoryId: null,
  departmentId: null,
  itemQuery: '',
  sivNo: '',
  ingredientFilter: 'all'
})

function money(val) {
  return Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 5 })
}


const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)
const printCompanyName = computed(() =>
  currentUser.value?.company?.name ||
  currentUser.value?.companyName ||
  currentUser.value?.company_name ||
  'Company'
)
const printCompanyLogo = computed(() =>
  currentUser.value?.company?.logo_url ||
  currentUser.value?.companyLogo ||
  currentUser.value?.company_logo ||
  ''
)
const isStartDateRequired = computed(() =>
  String(filters.value.itemQuery || '').trim() === '' &&
  String(filters.value.sivNo || '').trim() === ''
)

const itemSuggestions = computed(() => {
  return [...new Set([...(itemNames.value || []), ...(productNames.value || [])])]
})

function parseCsvNums(v) {
  if (v == null || v === '') return []
  return String(v).split(',').map(s => Number(String(s).trim())).filter(n => Number.isFinite(n))
}

function normalizeUnit(u) {
  return String(u || '').trim().toLowerCase().replace(/\s+/g, '')
}

function isLiterOrKg(u) {
  const unit = normalizeUnit(u)
  return unit === 'liter' || unit === 'litre' || unit === 'l' || unit === 'kg' || unit === 'k.g.' || unit === 'k.g' || unit === 'kilogram'
}

function getDisplayQty(qty, volume, unit) {
  const q = Number(qty || 0)
  const v = Number(volume || 0)
  if (isLiterOrKg(unit) && Number.isFinite(v) && v > 0) {
    return (q * v) / 1000
  }
  return q
}

function getDisplayUnitPrice(unitPrice, volume, unit) {
  const p = Number(unitPrice || 0)
  const v = Number(volume || 0)
  if (isLiterOrKg(unit) && Number.isFinite(v) && v > 0) {
    // If volume is stored in grams/ml, convert price to per KG/L.
    return (p / v) * 1000
  }
  return p
}

function toYmd(value) {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toISOString().split('T')[0]
}

async function loadLookups() {
  if (!companyId.value && !branchId.value) return
  const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
  try {
    const [depRes, catRes, itemRes, prodRes] = await Promise.all([
      apiFetch(`/departments?${query}`),
      apiFetch(`/categories?${query}`),
      apiFetch(`/items?${query}`),
      apiFetch(`/products?${query}`)
    ])
    if (depRes.ok) departments.value = await depRes.json().catch(() => [])
      departments.value.sort((a, b) => a.name.localeCompare(b.name))
    if (catRes.ok) categories.value = await catRes.json().catch(() => [])
         categories.value.sort((a, b) => a.name.localeCompare(b.name))
    if (itemRes.ok) {
      const items = await itemRes.json().catch(() => [])
      itemNames.value = Array.isArray(items) ? items.map(i => i?.itemName).filter(Boolean) : []
    }
    if (prodRes.ok) {
      const products = await prodRes.json().catch(() => [])
      productNames.value = Array.isArray(products) ? products.map(p => p?.name).filter(Boolean) : []
    }
  } catch (err) {
    console.error('Failed to load SIV report lookups', err)
  }
}

async function generateReport() {
  const hasLikeSearch = !isStartDateRequired.value
  if (!hasLikeSearch && !filters.value.startDate) {
    alert('Start date is required unless Item or SIV Number is filled')
    return
  }
  loading.value = true
  const queryParts = []
  if (branchId.value) queryParts.push(`branchId=${branchId.value}`)
  else if (companyId.value) queryParts.push(`companyId=${companyId.value}`)
  if (!hasLikeSearch && filters.value.startDate) queryParts.push(`startDate=${filters.value.startDate}`)
  if (!hasLikeSearch && filters.value.endDate) queryParts.push(`endDate=${filters.value.endDate}`)
  if (filters.value.categoryId) queryParts.push(`categoryId=${encodeURIComponent(filters.value.categoryId)}`)
  if (filters.value.departmentId) queryParts.push(`departmentId=${filters.value.departmentId}`)
  if (filters.value.itemQuery) queryParts.push(`itemQuery=${encodeURIComponent(filters.value.itemQuery)}`)
  if (filters.value.sivNo) queryParts.push(`sivNo=${encodeURIComponent(filters.value.sivNo)}`)
  if (filters.value.ingredientFilter === '1') queryParts.push('is_ingredient=1')
  else if (filters.value.ingredientFilter === '0') queryParts.push('is_ingredient=0')

  try {
    const q = `?${queryParts.join('&')}`
    const res = await apiFetch(`/siv/report${q}`)
    if (res.ok) report.value = await res.json().catch(() => [])
    else report.value = []
  } catch (err) {
    console.error('Failed to fetch SIV report', err)
    report.value = []
  }
  finally {
    loading.value = false
  }
  }

const rows = computed(() => {
  const out = []
  for (const s of report.value || []) {
    const sivNo = s?.sivNo || '-'
    const date = s?.date ? new Date(s.date).toISOString().split('T')[0] : '-'
    const departmentName = s?.department?.name || '-'
    const items = Array.isArray(s?.items) ? s.items : []
    for (const it of items) {
      const isProduct = !!it?.product || Number(it?.productId || 0) > 0
      const itemName = it?.item?.itemName || it?.product?.name || '-'
      const unit = isProduct ? (it?.product?.uom || '') : (it?.item?.unit || '')
      const categoryName = isProduct ? '-' : (it?.item?.category?.name || '-')
      const qtys = parseCsvNums(it?.quantityDetails)
      const prices = parseCsvNums(it?.unitPriceDetails)
      const vols = parseCsvNums(it?.variantDetails)
      const reqDate = toYmd(it?.approvedRequestItem?.request?.date)
      const apprDate = toYmd(it?.approvedRequestItem?.approvedAt)
      if (qtys.length > 0) {
        for (let i = 0; i < qtys.length; i++) {
          const q = Number(qtys[i] || 0)
          const p = Number(prices[i] ?? prices[prices.length - 1] ?? it?.unitPrice ?? 0)
          const v = Number(vols[i] ?? vols[vols.length - 1] ?? it?.variantDetails ?? 0)
          const displayQty = getDisplayQty(q, v, unit)
          const displayUnitPrice = getDisplayUnitPrice(p, v, unit)
          out.push({
            key: `${s?.id || sivNo}-${it?.id || itemName}-split-${i}`,
            sivNo,
            date,
            reqDate,
            apprDate,
            departmentName,
            categoryName,
            itemName,
            isProduct,
            volume: Number.isFinite(v) && v > 0 ? v : '-',
            unitPrice: displayUnitPrice,
            quantity: displayQty,
            totalPrice: displayQty * displayUnitPrice,
            remark: it?.remark || s?.remark || '-'
          })
        }
      } else {
        const q = Number(it?.quantity || 0)
        const v = Number(it?.variantDetails || 0)
        const displayQty = getDisplayQty(q, v, unit)
        const displayUnitPrice = getDisplayUnitPrice(Number(it?.unitPrice || 0), v, unit)
        out.push({
          key: `${s?.id || sivNo}-${it?.id || itemName}-single`,
          sivNo,
          date,
          reqDate,
          apprDate,
          departmentName,
          categoryName,
          itemName,
          isProduct,
          volume: Number.isFinite(v) && v > 0 ? v : '-',
          unitPrice: displayUnitPrice,
          quantity: displayQty,
          totalPrice: displayQty * displayUnitPrice,
          remark: it?.remark || s?.remark || '-'
        })
      }
    }
  }
  return out
})

function sortBy(key) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

const sortedRows = computed(() => {
  const list = [...rows.value]
  const modifier = sortOrder.value === 'asc' ? 1 : -1

  const getVal = (row) => row[sortKey.value]

  list.sort((a, b) => {
    const va = getVal(a)
    const vb = getVal(b)
    const na = typeof va === 'number' ? va : (va !== null && va !== undefined && va !== '' ? Number(va) : NaN)
    const nb = typeof vb === 'number' ? vb : (vb !== null && vb !== undefined && vb !== '' ? Number(vb) : NaN)
    if (!Number.isNaN(na) && !Number.isNaN(nb)) {
      return (na - nb) * modifier
    }
    const sa = (va ?? '').toString().toLowerCase()
    const sb = (vb ?? '').toString().toLowerCase()
    if (sa < sb) return -1 * modifier
    if (sa > sb) return 1 * modifier
    return 0
  })

  return list
})

const totalPriceSum = computed(() => {
  const sum = rows.value.reduce((acc, row) => acc + Number(row?.totalPrice || 0), 0)
  return Number.isFinite(sum) ? sum.toFixed(2) : '0.00'
})

function handlePrintReport() {
  if (!sortedRows.value.length) {
    alert('No data to print. Please generate the report first.')
    return
  }
  window.print()
}

onMounted(async () => {
  await loadLookups()
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 11px;
  line-height: 1.15;
  color: #1e293b;
}
th, td {
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  padding: 3px 4px;
  font-size: inherit;
}
th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}

.siv-table-wrap {
  min-height: 70vh;
}

@media print {
  :global(.sidebar),
  :global(header),
  :global(nav),
  :global(.top-bar),
  :global(.app-header),
  :global(.logout-btn) {
    display: none !important;
  }

  .print-hide { display: none !important; }

  body, .bg-\[\#f8fafc\] {
    background: transparent !important;
  }

  .overflow-x-auto {
    overflow: visible !important;
  }

  .print-container {
    box-shadow: none !important;
    border: none !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow: visible !important;
  }

  .print-table {
    width: 100% !important;
    border-collapse: collapse !important;
    border-spacing: 0 !important;
    table-layout: auto !important;
  }

  .print-table th, .print-table td {
    border: 0 !important;
    border-right: 1px solid #000 !important;
    border-bottom: 1px solid #000 !important;
    padding: 4px !important;
    text-align: left !important;
    color: #000 !important;
    font-size: 10px !important;
    white-space: normal !important;
    word-break: break-word !important;
  }

  .print-table tr > *:first-child {
    border-left: 1px solid #000 !important;
  }

  .print-table thead tr:first-child > * {
    border-top: 1px solid #000 !important;
  }

  .print-table th {
    background-color: #f3f3f3 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .print-doc-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  .print-doc-header-grid {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    column-gap: 12px;
  }

  .print-doc-left {
    justify-self: start;
  }

  .print-doc-title {
    justify-self: center;
    margin: 0 !important;
    white-space: nowrap;
  }

  .print-doc-right {
    justify-self: end;
  }

  .print-company-block {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .print-company-logo {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    display: block;
    object-fit: contain;
    border: 0 !important;
    border-radius: 0;
    padding: 0;
    background: #fff;
  }

  .print-company-logo-fallback {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    border: 1px solid #000;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: #334155;
    background: #fff;
  }

  .print-company-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .print-company-name {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.2;
  }

  .print-doc-meta {
    font-size: 12px;
    color: #0f172a;
    white-space: nowrap;
  }
}
</style>
